import { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import config from "@Config/index";
import useIsHappyFlow from "hooks/useIsHappyFlow";
import { AppState } from "@Store/index";
import {
  buildListABPlan,
  type BreakerMap,
  type PageBreakerState,
  type ViewMode,
} from "./filteredBreakerPlacement";

type Params = {
  areFiltersApplied: boolean;
  useLegacyBreakerPath: boolean;
  isMapView: boolean;
  showGridView: boolean;
  isDesktopList: boolean;
  metaCount?: number;
  isRadiusExpanded: boolean;
  showRadiusExpandDivider: boolean;
  radiusExpandSplitIndex: number;
  radiusExpandPrimaryCount?: number | null;
  nearbyResultsCount: number;
};

export type FilteredBreakerPlans = {
  listA: BreakerMap;
  listB: BreakerMap;
  stateAfterAB: PageBreakerState;
  viewMode: ViewMode;
};

const useFilteredBreakerPlans = ({
  areFiltersApplied,
  useLegacyBreakerPath,
  isMapView,
  showGridView,
  isDesktopList,
  metaCount,
  isRadiusExpanded,
  showRadiusExpandDivider,
  radiusExpandSplitIndex,
  radiusExpandPrimaryCount,
  nearbyResultsCount,
}: Params): FilteredBreakerPlans | null => {
  const isHappyFlow = useIsHappyFlow();
  const regionCanonical = useSelector(
    ({ searchPageData }: AppState) => searchPageData.region?.canonical_name,
    shallowEqual,
  );
  const getHelpClicked = useSelector(
    ({ userHistory }: AppState) => userHistory.getHelpClicked,
    shallowEqual,
  );

  const getHelpDismissedForCity = Boolean(
    isHappyFlow && regionCanonical && getHelpClicked.includes(regionCanonical),
  );

  return useMemo(() => {
    if (!areFiltersApplied || useLegacyBreakerPath || isMapView) return null;

    let viewMode: ViewMode = "mobileList";
    if (showGridView) viewMode = "desktopGrid";
    else if (isDesktopList) viewMode = "desktopList";

    const listATotal =
      isRadiusExpanded && showRadiusExpandDivider
        ? Number(radiusExpandPrimaryCount ?? radiusExpandSplitIndex ?? 0)
        : Number(metaCount ?? 0);

    const listBTotal = showRadiusExpandDivider ? nearbyResultsCount : 0;

    const { listA, listB, stateAfterAB } = buildListABPlan({
      listATotal,
      listBTotal,
      ctx: {
        viewMode,
        isChinaApp: Boolean(config.IS_CHINA_APP),
        getHelpDismissedForCity,
      },
    });

    return { listA, listB, stateAfterAB, viewMode };
  }, [
    areFiltersApplied,
    useLegacyBreakerPath,
    isMapView,
    showGridView,
    isDesktopList,
    metaCount,
    isRadiusExpanded,
    showRadiusExpandDivider,
    radiusExpandSplitIndex,
    radiusExpandPrimaryCount,
    nearbyResultsCount,
    getHelpDismissedForCity,
  ]);
};

export default useFilteredBreakerPlans;
